import {
  Cart,
  CartFactory,
  Checkout,
  CountriesFactory,
  CurrencyCode,
  GtinItem,
  ICartPromotion,
  ICartPosition,
  ICountriesLookup,
  ICartRequestHandlerPutCartOptions,
} from "../../../typescript-library";
import * as React from "react";
import {PropsWithChildren, useEffect, useState} from "react";
import {UrlSearchParamsHelper} from "../../../helpers/urlSearchParamsHelper";
import {getShopId} from "../../lib/getShopId";
import window from "../../lib/WindowExtended";
import {IProductTrackingData, Tracking} from "../../../tracking/tracking";
import {sentryAddBreadcrumb} from "../../../helpers/sentryHelper";
import {getUtilizationMessage} from "../../../helpers/utilizationMessageHelper";
import {CartErrorType, retrieveErrorMessages} from "../../../helpers/errorHandling";
import {useCustomerSelector} from "../../../store";
import {FUNDING_SOURCE} from "@paypal/paypal-js";
import braintree, {GooglePaymentTokenizePayload} from "braintree-web";
import {PaymentController} from "../../lib/payments/PaymentController";
import {cartContentKey, cartStorage, restoreCart} from "../../lib/payments/decideCartContent";
import {PlaceType} from "../../lib/payments/paypal/helpers/paypalTypes";
import {FriendlyCaptchaSDK} from "@friendlycaptcha/sdk";

export type GooglePayDataType = {
  tokenData: GooglePaymentTokenizePayload;
  paymentData: google.payments.api.PaymentData;
};

export type BraintreeDataType = {client: braintree.Client; token: string};

export type PaymentInfoType = {
  paymentSource: FUNDING_SOURCE;
  nonce: string;
  place: PlaceType;
};

export interface IViewState {
  isRegistering: boolean;
  shippingErrors: string[];
  paypalInfos: PaymentInfoType | undefined;
  gPayData: GooglePayDataType | undefined;
  appleData: PaymentInfoType | undefined;
}

export interface ICartContext extends Omit<ICartContextNoCart, "requestCart">{
  cart: Cart;

  isIntoCheckout: boolean;
  setIsIntoCheckout: (isIntoCheckout: boolean, checkout?: Checkout) => void;

  setViewState: (viewState?: IViewState) => void;
  viewState: IViewState;

  setCartErrors: (errors: CartErrorType[]) => void;
  cartErrors: CartErrorType[];

  setExternalErrors: (errors: CartErrorType[]) => void;
  externalErrors: CartErrorType[];

  frcSdk: FriendlyCaptchaSDK;
}

interface IRequestCart {
  cart: Cart;
  done: () => void;
}

export interface ICartContextNoCart {
  requestCart: () => Promise<IRequestCart>;

  isCartVisible: boolean;
  setIsCartVisible: (isCartVisible: boolean) => void;

  setItemsAdded: (itemIds: GtinItem[]) => void;
  itemsAdded: GtinItem[];

  setVoucherAdded: (voucherAdded: string) => void;
  voucherAdded: string;

  countries: ICountriesLookup[];
  braintreeData: BraintreeDataType | undefined;
  utilizationMessage: string | undefined;
  loading: boolean;
}

export type IUnsafeCartContext = ICartContext | ICartContextNoCart | null;

const unsafeCartContext = React.createContext<IUnsafeCartContext>(null);

export const isCart = (cart: IUnsafeCartContext): cart is ICartContext => {
  return (cart as ICartContext).cart !== undefined;
};

export const useCartContext = (): ICartContext => {
  const cartContext = React.useContext(unsafeCartContext);
  if (!isCart(cartContext)) {
    throw new Error("useCartContext must be used in a component within a CartContextProvider.");
  }

  return cartContext;
};

export const useUnsafeCartContext = (): IUnsafeCartContext => {
  return React.useContext(unsafeCartContext);
};

const CartContextProvider: React.FC<PropsWithChildren> = (props) => {
  const setItemsAdded = (itemsAdded: GtinItem[]) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, itemsAdded};
    });
  };
  /**
   * sets added voucher, to open the popup
   * @param voucherAdded voucher code string that was added to trigger voucher popup
   */
  const setVoucherAdded = (voucherAdded: string) => {
    setState((prevState) => {
      if (!prevState) {
        return null;
      }

      return {...prevState, voucherAdded};
    });
  };

  const setIsIntoCheckout = (isIntoCheckout: boolean, checkout?: Checkout) => {
    if (
      !isIntoCheckout &&
      checkout?.cartData.positions.find((pos) => pos.type === "Shipping")?.reference?.shippingClassId !==
        cartRef.current?.positions.find((pos) => pos.type === "Shipping")?.reference?.shippingClassId
    ) {
      void cartRef.current?.get().then(() => setPartialCartContextState({isIntoCheckout}));
    } else {
      setPartialCartContextState({isIntoCheckout, cartErrors: []});
    }
  };

  const setCartErrors = (cartErrors: CartErrorType[]) => setPartialCartContextState({cartErrors});

  const setExternalErrors = (externalErrors: CartErrorType[]) => setPartialCartContextState({externalErrors});

  /**
   * get promotion params if existing
   */
  const getPromotionData = (): ICartPromotion | undefined => {
    if (!window.c_PromotionId) {
      return undefined;
    }

    return {
      id: Number(window.c_PromotionId),
      reference: window.c_PromotionContentReference || undefined,
    };
  };

  const cartContextNoCart: ICartContextNoCart = {
    requestCart: async (): Promise<IRequestCart> => {
      const activeCountry = window.c_ActiveCountry;
      const activeCurrency = window.c_ActiveCurrency as CurrencyCode;
      const promotionData = getPromotionData();

      const cartPromise = CartFactory.generateCart(getShopId(), customer.visitorId, {
        countryCode: activeCountry,
        currencyCode: activeCurrency,
        promotion: promotionData,
        isCorporateCustomer: customer.isCorporateCustomer,
      });

      const countriesPromise = CountriesFactory.getAllCountries(getShopId())
        .then((countries) => countries.filter((country) => country.isDeliveryAllowed))
        .catch(() => [] as ICountriesLookup[]);

      const utilizationMessagePromise = getUtilizationMessage().catch(() => undefined);

      return await Promise.all([cartPromise, countriesPromise, utilizationMessagePromise]).then((results) => {
        // this must be called by the callback once the fallback is done
        const done = () => {
          updateCart(...results);
        };

        return {cart: results[0], done};
      });
    },
    setItemsAdded,
    itemsAdded: [],

    utilizationMessage: undefined,

    setVoucherAdded,
    voucherAdded: "",

    setIsCartVisible: (isCartVisible: boolean) =>
      setState((prevState) => {
        if (!prevState) {
          return null;
        }

        return {...prevState, isCartVisible};
      }),
    isCartVisible: false,

    countries: [],

    braintreeData: undefined,

    loading: true,
  };

  const [state, setState] = useState<IUnsafeCartContext>(cartContextNoCart);
  const {customer} = useCustomerSelector();

  // We need this ref to use it in clean up useEffect callback
  const cartRef = React.useRef<Cart | null>(null);

  const setPartialCartContextState = (partialState: Partial<ICartContext>) => {
    setState((previousState) => {
      if (!isCart(previousState)) {
        return null;
      }

      return {...previousState, ...partialState};
    });
  };

  const trackCartItems = (positions: ICartPosition[]) => {
    const positionsToTrack = positions.reduce<IProductTrackingData[]>((akkumulator, current) => {
      if (current.type === "Item" && !!current.item && !!current.reference?.itemId) {
        akkumulator.push({
          name: current.text,
          id: current.item.itemId,
          brand: current.item.manufacturer,
          price: current.price.amount,
          quantity: current.quantity,
        });
      }

      return akkumulator;
    }, []);

    Tracking.setCartItems(positionsToTrack);
  };

  const refreshCart = () => {
    if (cartRef.current) {
      trackCartItems(cartRef.current.positions);
      const cartErrors = retrieveErrorMessages(cartRef.current);
      setPartialCartContextState({cartErrors});
    } else {
      setPartialCartContextState({});
    }
  };

  useEffect(() => {
    const requestCartForRestoration = async () => {
      await cartContextNoCart.requestCart().then(async ({cart, done}) => {
        await restoreCart(cart);
        done();
      });
    };

    const requestCartForParams = async () => {
      await cartContextNoCart.requestCart().then(async ({cart, done}) => {
        const itemsAdded: GtinItem[] = UrlSearchParamsHelper.itemIds.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
          gtin: null,
        }));
        let voucherAdded = "";
        if (itemsAdded.length) {
          await cart.addMultipleItems(itemsAdded);
        }

        if (UrlSearchParamsHelper.voucherCode) {
          voucherAdded = UrlSearchParamsHelper.voucherCode;
          await cart.addVoucher(UrlSearchParamsHelper.voucherCode);
        }

        done();

        if (state && (itemsAdded.length > 0 || voucherAdded !== "")) {
          setState((prevState) => {
            if (!prevState) {
              return null;
            }

            return {...prevState, itemsAdded, voucherAdded};
          });
        }
      });
    };

    const getCartIfExisting = async () => {
      /* starting three independent, parallel async requests */

      // request cart and possibly update its settings, if cart exists
      void CartFactory.getCartForVisitorId(getShopId(), customer.visitorId).then(async (existingCart) => {
        if (!existingCart) {
          setState((prevState) => prevState && {...prevState, loading: false});

          return;
        }

        // region update currency code and promotion id if necessary

        const promotion = getPromotionData();
        const {isCorporateCustomer} = customer;
        const cartParams: ICartRequestHandlerPutCartOptions = {};

        if (promotion && promotion.id !== existingCart.promotion?.id) {
          cartParams.promotion = promotion;
        }
        if (existingCart.isCorporateCustomer !== isCorporateCustomer) {
          cartParams.isCorporateCustomer = isCorporateCustomer;
        }
        if (Object.keys(cartParams).length) {
          await existingCart.put(cartParams);
        }

        // endregion

        updateCart(existingCart, []);
      });

      // request delivery countries for current shop
      const countriesPromise = CountriesFactory.getAllCountries(getShopId())
        .then((countries) => countries.filter((country) => country.isDeliveryAllowed))
        .catch(() => [] as ICountriesLookup[]);

      //  request possible utilizationMessage
      const utilizationMessagePromise = getUtilizationMessage().catch(() => undefined);

      // wait for all requests to return
      await Promise.all([countriesPromise, utilizationMessagePromise]).then(([countries, utilizationMessage]) => {
        setState((prevState) => {
          if (!prevState) return null;

          return {...prevState, countries, utilizationMessage};
        });
      });
    };

    // Downloads and sets the bt token in here, above the `PayPalScriptProvider`,
    // so then, the `BraintreeButton` (below/inside the `PayPalScriptProvider`)
    // can dispatch it into the `PayPalScriptProvider` options
    void PaymentController.initBraintreeClient(window.c_ActiveCurrency as CurrencyCode).then((braintreeData) => {
      setState((prevState) => {
        if (!prevState) {
          return null;
        }

        return {...prevState, braintreeData};
      });
    });

    if (UrlSearchParamsHelper.containsParams()) {
      void requestCartForParams();
    } else if (cartStorage.has(cartContentKey)) {
      void requestCartForRestoration();
    } else {
      void getCartIfExisting();
    }

    customer.emitter.on(getCartIfExisting);

    // clean up of event listeners to prevent setting state in unmounted components
    return () => {
      customer.emitter.off(getCartIfExisting);
      if (!cartRef.current) {
        return;
      }
      cartRef.current.emitter.off(refreshCart);
    };
  }, []);

  useEffect(() => {
    if (state?.isCartVisible) {
      Tracking.sendEvent("trackCartPopupOpened");
    }
  }, [state?.isCartVisible]);

  useEffect(() => {
    if (state && state.itemsAdded.length && isCart(state)) {
      const addedPositions = state.cart.positions
        .filter<ICartPosition>(
          (pos): pos is ICartPosition =>
            pos.type === "Item" &&
            !!pos.reference?.itemId &&
            !!pos.item &&
            state.itemsAdded.some(
              ({itemId, gtin}) => itemId === pos.item?.itemId || gtin === (pos.item?.registrationNumber ?? undefined),
            ),
        )
        .map<IProductTrackingData>((pos) => ({
          brand: pos.item?.manufacturer ?? null,
          id: pos.reference?.itemId ?? 0,
          name: pos.text,
          price: pos.price.amount,
          quantity: pos.quantity,
        }));

      if (addedPositions.length) {
        Tracking.sendAddToCart(addedPositions, state.cart.currencyCode);
      }
    }
  }, [state?.itemsAdded]);

  // this is only called when the cart is being initialized
  const updateCart = (cart: Cart, countries: ICountriesLookup[], utilizationMessage?: string): void => {
    cart.emitter.on(refreshCart);
    cartRef.current = cart;
    trackCartItems(cart.positions);
    const frcSdk = new FriendlyCaptchaSDK();

    setState((prevState) => {
      return {
        cart,

        // the following is not really my doing
        isIntoCheckout: prevState && isCart(prevState) ? prevState.isIntoCheckout : false,
        setIsIntoCheckout,

        isCartVisible: !!prevState?.isCartVisible,
        setIsCartVisible: (isCartVisible: boolean) => {
          sentryAddBreadcrumb(
            isCartVisible ? "opening cart" : "closing cart",
            null,
            "setIsCartVisible",
            undefined,
            customer,
          );

          setPartialCartContextState({isCartVisible});
        },

        itemsAdded: prevState ? prevState.itemsAdded : [],
        setItemsAdded,

        setVoucherAdded,
        voucherAdded: prevState ? prevState.voucherAdded : "",

        setViewState: (viewState: IViewState | undefined) => setPartialCartContextState({viewState}),
        viewState: {
          isRegistering: false,
          shippingErrors: [],
          paypalInfos: undefined,
          gPayData: undefined,
          appleData: undefined,
        },

        utilizationMessage: utilizationMessage || prevState?.utilizationMessage,

        cartErrors: retrieveErrorMessages(cart),
        setCartErrors,

        externalErrors: isCart(prevState) ? prevState.externalErrors : [],
        setExternalErrors,

        frcSdk,

        countries: countries.length ? countries : (prevState?.countries ?? []),

        braintreeData: prevState?.braintreeData,

        loading: false,
      };
    });
  };

  const {Provider} = unsafeCartContext;

  return <Provider value={state}>{props.children}</Provider>;
};

export default CartContextProvider;
